import seek, { seekTypes } from '@adc/seek'
import type { CollectionResult } from '@adc/sdk/types'
import * as blueprint from '@adc/blueprint'
import { QueryType, Coordinates } from './'
import { match } from 'ts-pattern'

type FetchResult = {
  data: CollectionResult
  errors: Record<string, unknown>[]
}

const getDSLForShowcaseListings = (listingIds: string[]) => ({
  and: {
    expressions: [
      {
        equals: {
          fields: 'listing_configuration.product_type.keyword',
          values: ['PRIVATE_SELLER'],
        },
      },
      {
        equals: {
          fields: 'listing_id.keyword',
          values: listingIds,
        },
      },
    ],
  },
})

const getDSL = (queryType: QueryType) =>
  match(queryType)
    .with('privateSellerLand', () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'listing_configuration.product_type.keyword',
              values: ['PRIVATE_SELLER'],
            },
          },
          {
            equals: {
              fields: 'primary_property_summary.structure_type_code.keyword',
              values: ['LAND'],
            },
          },
        ],
      },
    }))
    .with('privateSellerNoLand', () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'listing_configuration.product_type.keyword',
              values: ['PRIVATE_SELLER'],
            },
          },
          {
            not: {
              expression: {
                equals: {
                  fields:
                    'primary_property_summary.structure_type_code.keyword',
                  values: ['LAND'],
                },
              },
            },
          },
        ],
      },
    }))
    .with('privateSellerAll', () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'listing_configuration.product_type.keyword',
              values: ['PRIVATE_SELLER'],
            },
          },
        ],
      },
    }))
    .otherwise(() => ({
      all: {},
    }))

type FieldConfig = Parameters<seek['fields']>[1]

const fieldsConfig: FieldConfig = {
  fragmentDefinitions: {
    ListingSearchFields: {
      fragment: blueprint.listing.fragments.listingCard.definition,
    },
  },
}

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_OFFSET = 0

interface getMarketingPrivateSellerListingsParams {
  queryType?: QueryType
  showcaseListings?: string[]
  coordinates: Coordinates
}

const fragmentNameWithoutLeadingDots =
  blueprint.listing.fragments.listingCard.reference.replace(/^\.{3}/, '')

export const getMarketingPrivateSellerListings = ({
  queryType,
  showcaseListings,
  coordinates,
}: getMarketingPrivateSellerListingsParams): Promise<FetchResult> => {
  let dsl
  if (queryType) {
    dsl = getDSL(queryType)
  } else if (showcaseListings) {
    dsl = getDSLForShowcaseListings(showcaseListings)
  } else {
    throw new Error('queryType or showcaseListings is required')
  }
  const seeker = seek
    .for(seekTypes.LISTINGS)
    .operationName('searchPrivateSellerListings')
    // @ts-ignore dsl doesn't match type
    .dsl(dsl)
    .fields([`fragment:${fragmentNameWithoutLeadingDots}`], fieldsConfig)
    .sortByGeo(
      'primary_property_summary.address.coordinates',
      {
        lat: coordinates.latitude,
        lon: coordinates.longitude,
      },
      'DESC'
    )
    .sortByField('auction.auction_date', 'ASC')
    .size(DEFAULT_PAGE_SIZE)
    .offset(DEFAULT_OFFSET)

  return seeker.execute().then(
    ({
      data,
      errors,
    }: {
      data: {
        seek: CollectionResult
      }
      errors: Record<string, unknown>[]
    }) => {
      const result = {
        data: data.seek,
        errors,
      }
      return result
    }
  )
}
